import { SubscriptionName } from '@anm/api/modules/subscription';
import UpgradeSubscriptionModal from '@anm/components/subscription/UpgradeSubscriptionModal';
import isTeamSubscription from '@anm/helpers/subscription/isTeamSubscription';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import vtConfig from '../../../config';
import mapCardType from '../../../helpers/user/mapCardType';
import useChangePeriodHandler from '../../../hooks/subscription/useChangePeriodHandler';
import useUserCard from '../../../hooks/user/useUserCard';
import { pricingSelectors } from '../../../store/modules/pricing';
import { subscriptionSelectors } from '../../../store/modules/subscription';
import { userSelectors } from '../../../store/modules/user';

import useCoupon from './hooks/useCoupon';
import useErrors from './hooks/useErrors';
import usePredictPrice from './hooks/usePredictPrice';
import usePurchaseHandler from './hooks/usePurchaseHandler';

export type UpgradeUserSubscriptionProps = {
  subscriptionName: SubscriptionName;
  onModalClose?: () => void;
};

const { ssl } = vtConfig();

const UpgradeUserSubscription: FC<UpgradeUserSubscriptionProps> = ({ subscriptionName, onModalClose }) => {
  const errors = useErrors();
  const [coupon, setCoupon] = useCoupon();
  const card = useUserCard();
  const profile = useSelector(userSelectors.selectUserProfile);
  const hasUserStripe = useSelector(userSelectors.selectUserStripeStatus);
  const selectedPricingPeriod = useSelector(pricingSelectors.selectCurrentPeriod);

  const handleChangePeriod = useChangePeriodHandler();
  const currentSubscription = useSelector(userSelectors.selectWaveSubscription);
  const [handlerPurchase, isSubscriptionChangePending] = usePurchaseHandler(subscriptionName);

  const subscription = useSelector(state => subscriptionSelectors.selectSubscriptionByName(state, subscriptionName));

  const [totalPrice, predictDiscount, isPredictPricePending] = usePredictPrice({
    coupon,
    period: selectedPricingPeriod,
    subscriptionName: subscription?.name
  });

  if (!profile || !subscription || !currentSubscription) {
    return null;
  }

  const { annualPrice, monthlyPrice, displayName, name: toSubscriptionName } = subscription || {};

  const { last4 = '', type = '' } = card || {};
  const { name: currentSubscriptionName, billingPeriod: currentPeriod } = currentSubscription;

  const disablePeriod = currentSubscriptionName === toSubscriptionName ? currentPeriod : undefined;

  return (
    <UpgradeSubscriptionModal
      sslId={ssl}
      errors={errors}
      email={profile.email}
      coupon={coupon}
      planName={displayName}
      discount={predictDiscount}
      cardType={mapCardType(type)}
      cardLast4={last4}
      totalPrice={totalPrice}
      annualPrice={annualPrice}
      monthlyPrice={monthlyPrice}
      disablePeriod={disablePeriod}
      selectedPeriod={selectedPricingPeriod}
      isSubmitPending={isSubscriptionChangePending}
      isPredictPricePending={isPredictPricePending}
      showEditCardBlock={!hasUserStripe || (!profile.teamId && isTeamSubscription(subscriptionName))}
      onPurchase={handlerPurchase}
      onModalClose={onModalClose}
      onCouponApply={setCoupon}
      onPeriodChange={handleChangePeriod}
    />
  );
};

export default UpgradeUserSubscription;
